import { LoginRequest, AuthResponse, User, UserStats, CreateUserRequest, UpdateUserRequest, ChangePasswordRequest, RegisterRequest, RegistrationResult, UpdateProfileRequest, EmailCodeLoginOptions } from '@radevents/shared';

const API_V1 = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080') + '/api/v1';

/**
 * Fehler mit dem Fehlercode des Backends. Die Anmeldung nutzt ihn, um den Sonderfall
 * "E-Mail noch nicht bestätigt" von einem falschen Passwort zu unterscheiden.
 */
export class AuthError extends Error {
  constructor(message: string, public code?: string, public email?: string) {
    super(message);
    this.name = 'AuthError';
  }
}

class AuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private user: User | null = null;

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        this.user = JSON.parse(userStr);
      } catch (e) {
        console.error('Failed to parse user from localStorage:', e);
        this.clearStorage();
      }
    }
  }

  private saveToStorage(authResponse: AuthResponse) {
    this.accessToken = authResponse.accessToken;
    this.refreshToken = authResponse.refreshToken;
    this.user = authResponse.user;

    localStorage.setItem('accessToken', authResponse.accessToken);
    localStorage.setItem('refreshToken', authResponse.refreshToken);
    localStorage.setItem('user', JSON.stringify(authResponse.user));
  }

  private clearStorage() {
    this.accessToken = null;
    this.refreshToken = null;
    this.user = null;

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  /**
   * Registrierung mit Passwort. Meldet NICHT an — das Konto wird erst mit dem Klick im
   * Bestätigungslink nutzbar (Double-Opt-in), siehe {@link confirmEmail}.
   */
  async register(request: RegisterRequest): Promise<RegistrationResult> {
    const response = await fetch(`${API_V1}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Registrierung fehlgeschlagen');
    }

    return response.json();
  }

  /** Bestätigungslink einlösen: schaltet das Konto frei und meldet direkt an. */
  async confirmEmail(token: string): Promise<AuthResponse> {
    const response = await fetch(`${API_V1}/auth/email/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new AuthError(
        error.error || 'Dieser Bestätigungslink ist ungültig oder abgelaufen.',
        error.code
      );
    }

    const authResponse: AuthResponse = await response.json();
    this.saveToStorage(authResponse);
    return authResponse;
  }

  /** Bestätigungsmail erneut anfordern. Antwortet immer gleich (keine Konto-Auskunft). */
  async resendConfirmation(email: string): Promise<void> {
    await fetch(`${API_V1}/auth/email/resend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  }

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await fetch(`${API_V1}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new AuthError(error.error || 'Anmeldung fehlgeschlagen', error.code, error.email);
    }

    const authResponse: AuthResponse = await response.json();
    this.saveToStorage(authResponse);
    return authResponse;
  }

  /** Passwordless login, step 1: request a one-time code for an e-mail or username. Never reveals if the account exists. */
  async requestEmailCode(identifier: string): Promise<void> {
    const response = await fetch(`${API_V1}/auth/email/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier }),
    });
    if (!response.ok) {
      throw new Error('Code konnte nicht gesendet werden. Bitte später erneut versuchen.');
    }
  }

  /** Passwordless login, step 2: exchange the code for a session. */
  async verifyEmailCode(identifier: string, code: string, origin?: EmailCodeLoginOptions): Promise<AuthResponse> {
    const response = await fetch(`${API_V1}/auth/email/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Die Herkunft zählt nur, wenn dabei ein Konto entsteht (V183).
      body: JSON.stringify({ identifier, code, ...origin }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Code ungültig oder abgelaufen');
    }
    const authResponse: AuthResponse = await response.json();
    this.saveToStorage(authResponse);
    return authResponse;
  }

  async refreshTokens(): Promise<AuthResponse | null> {
    if (!this.refreshToken) {
      return null;
    }

    try {
      const response = await fetch(`${API_V1}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (!response.ok) {
        this.clearStorage();
        return null;
      }

      const authResponse: AuthResponse = await response.json();
      this.saveToStorage(authResponse);
      return authResponse;
    } catch (error) {
      this.clearStorage();
      return null;
    }
  }

  async logout(): Promise<void> {
    try {
      if (this.accessToken) {
        await fetch(`${API_V1}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      this.clearStorage();
    }
  }

  async makeAuthenticatedRequest(url: string, options: RequestInit = {}): Promise<Response> {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.accessToken) {
      (headers as any)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    let response = await fetch(url, {
      ...options,
      headers,
    });

    // If we get a 401 or 403, try to refresh the token
    if ((response.status === 401 || response.status === 403) && this.refreshToken) {
      const refreshResult = await this.refreshTokens();
      if (refreshResult) {
        (headers as any)['Authorization'] = `Bearer ${this.accessToken}`;
        response = await fetch(url, {
          ...options,
          headers,
        });
      }
    }

    return response;
  }

  // User management methods (admin only)
  async getAllUsers(): Promise<User[]> {
    const response = await this.makeAuthenticatedRequest(`${API_V1}/users`);

    if (!response.ok) {
      throw new Error('Failed to load users');
    }

    return response.json();
  }

  /**
   * Nutzerzahlen ohne Personenbezug. Anders als der Rest der Nutzerverwaltung auch fuer
   * Moderatoren freigegeben — sie sehen den Kopf der Nutzerseite, nicht die Kontenliste.
   */
  async getUserStats(): Promise<UserStats> {
    const response = await this.makeAuthenticatedRequest(`${API_V1}/users/stats`);

    if (!response.ok) {
      throw new Error('Failed to load user stats');
    }

    return response.json();
  }

  async getUserById(id: number): Promise<User> {
    const response = await this.makeAuthenticatedRequest(`${API_V1}/users/${id}`);

    if (!response.ok) {
      throw new Error('User not found');
    }

    return response.json();
  }

  async createUser(userData: CreateUserRequest): Promise<User> {
    const response = await this.makeAuthenticatedRequest(`${API_V1}/users`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create user');
    }

    return response.json();
  }

  async updateUser(id: number, userData: UpdateUserRequest): Promise<User> {
    const response = await this.makeAuthenticatedRequest(`${API_V1}/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update user');
    }

    return response.json();
  }

  async deleteUser(id: number): Promise<void> {
    const response = await this.makeAuthenticatedRequest(`${API_V1}/users/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete user');
    }
  }

  /**
   * Change password for the currently authenticated user.
   * Uses the /auth/change-password endpoint (no admin role required).
   */
  async changePassword(passwordData: ChangePasswordRequest): Promise<void> {
    const response = await this.makeAuthenticatedRequest(`${API_V1}/auth/change-password`, {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to change password');
    }

    // Der Wechsel widerruft serverseitig alle Sitzungen und gibt fuer diese hier neue Tokens
    // zurueck. Ohne sie zu uebernehmen wuerde der naechste Request abgemeldet enden.
    const data = await response.json().catch(() => null);
    if (data?.accessToken && data?.refreshToken && this.user) {
      this.saveToStorage({ accessToken: data.accessToken, refreshToken: data.refreshToken, user: this.user });
    }
  }

  /**
   * Löscht das eigene Konto unwiderruflich (DSGVO-Recht auf Löschung). Bei Erfolg wird die lokale
   * Session geleert — der Aufrufer muss den Nutzer anschließend ausloggen/umleiten.
   */
  async deleteAccount(): Promise<void> {
    const response = await this.makeAuthenticatedRequest(`${API_V1}/auth/me`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Konto konnte nicht gelöscht werden');
    }

    this.clearStorage();
  }

  async updateProfile(data: UpdateProfileRequest): Promise<User> {
    const response = await this.makeAuthenticatedRequest(`${API_V1}/auth/profile`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Profil konnte nicht aktualisiert werden');
    }

    const updatedUser = await response.json();
    this.user = updatedUser;
    localStorage.setItem('user', JSON.stringify(updatedUser));
    return updatedUser;
  }

  /**
   * Admin: Change password for any user by ID.
   * Uses the /users/{id}/change-password endpoint (admin role required).
   */
  async changeUserPassword(userId: number, passwordData: ChangePasswordRequest): Promise<void> {
    const response = await this.makeAuthenticatedRequest(`${API_V1}/users/${userId}/change-password`, {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to change password');
    }
  }

  // Getters
  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  getCurrentUser(): User | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return this.accessToken !== null && this.user !== null;
  }

  isAdmin(): boolean {
    return this.user?.role === 'ADMIN';
  }

  isModerator(): boolean {
    return this.user?.role === 'MODERATOR';
  }

  isAdminOrModerator(): boolean {
    return this.user?.role === 'ADMIN' || this.user?.role === 'MODERATOR';
  }

  hasRole(role: string): boolean {
    return this.user?.role === role;
  }
}

export const authService = new AuthService();
export default authService;
